import React, { useState, useEffect, useContext, useMemo, useCallback, useRef, memo } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, Package, Boxes, Wrench, CheckSquare, Square, Check } from 'lucide-react';
import { ConfirmDialog } from '@/components/ConfirmDialog';

const PAGE_SIZE = 50;

const ToolRow = memo(({ tool, isSelected, quantity, onToggle, onQuantityChange }) => (
  <div
    data-testid={`kit-tool-row-${tool.id}`}
    className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors rounded-md ${
      isSelected ? 'bg-primary/5 border-l-4 border-l-primary' : 'hover:bg-muted/50 border-l-4 border-l-transparent'
    }`}
    onClick={() => onToggle(tool.id)}
  >
    <div className={`w-5 h-5 shrink-0 rounded border-2 flex items-center justify-center transition-colors ${
      isSelected ? 'bg-primary border-primary' : 'border-muted-foreground/30'
    }`}>
      {isSelected && <Check className="w-3 h-3 text-primary-foreground" />}
    </div>
    {tool.photo ? (
      <img src={tool.photo} alt={tool.name} className="w-12 h-12 object-contain bg-muted rounded-lg shrink-0 border" loading="lazy" />
    ) : (
      <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center shrink-0 border">
        <Package className="w-6 h-6 text-muted-foreground" />
      </div>
    )}
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className="font-medium truncate text-sm">{tool.name}</span>
        <Badge variant="outline" className="text-xs shrink-0">{tool.type}</Badge>
      </div>
      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
        <span>N° {tool.article_number}</span>
        <Badge variant={tool.quantity_available > 0 ? 'secondary' : 'destructive'} className="text-xs">
          Stock: {tool.quantity_available}/{tool.quantity_total}
        </Badge>
      </div>
    </div>
    {isSelected && (
      <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
        <Label className="text-xs">Qté:</Label>
        <Input
          type="number"
          min="1"
          max={tool.quantity_available}
          value={quantity}
          onChange={(e) => onQuantityChange(tool.id, parseInt(e.target.value) || 1)}
          className="w-16 h-8 text-center rounded"
        />
      </div>
    )}
  </div>
));
ToolRow.displayName = 'ToolRow';

const ToolKits = () => {
  const { user } = useContext(AuthContext);
  const [kits, setKits] = useState([]);
  const [tools, setTools] = useState([]);
  const [filteredKits, setFilteredKits] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [toolSearchTerm, setToolSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteKitId, setDeleteKitId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  // Use object instead of Set for stable identity
  const [selectedMap, setSelectedMap] = useState({});
  const [toolQuantities, setToolQuantities] = useState({});
  const [formData, setFormData] = useState({ name: '', description: '' });

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    const filtered = kits.filter((kit) =>
      kit.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (kit.description && kit.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredKits(filtered);
  }, [searchTerm, kits]);

  const loadData = async () => {
    try {
      const [kitsRes, toolsRes] = await Promise.all([
        axios.get(`${API}/kits`),
        axios.get(`${API}/tools`)
      ]);
      setKits(kitsRes.data);
      setFilteredKits(kitsRes.data);
      setTools(toolsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = useCallback(() => {
    setFormData({ name: '', description: '' });
    setSelectedMap({});
    setToolQuantities({});
    setEditingId(null);
    setToolSearchTerm('');
  }, []);

  const filteredTools = useMemo(() => {
    const s = toolSearchTerm.toLowerCase();
    return tools.filter(t =>
      t.name.toLowerCase().includes(s) ||
      t.type?.toLowerCase().includes(s) ||
      t.article_number?.toLowerCase().includes(s)
    );
  }, [tools, toolSearchTerm]);

  const toggleToolSelection = useCallback((toolId) => {
    setSelectedMap(prev => {
      const next = { ...prev };
      if (next[toolId]) {
        delete next[toolId];
      } else {
        next[toolId] = true;
        setToolQuantities(q => ({ ...q, [toolId]: q[toolId] || 1 }));
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    const newSelected = {};
    const newQtys = {};
    filteredTools.forEach(t => {
      newSelected[t.id] = true;
      newQtys[t.id] = 1;
    });
    setSelectedMap(newSelected);
    setToolQuantities(prev => ({ ...prev, ...newQtys }));
  }, [filteredTools]);

  const deselectAll = useCallback(() => { setSelectedMap({}); }, []);

  const updateToolQuantity = useCallback((toolId, quantity) => {
    setToolQuantities(prev => ({ ...prev, [toolId]: Math.max(1, quantity) }));
  }, []);

  const selectedCount = Object.keys(selectedMap).length;
  const allSelected = filteredTools.length > 0 && filteredTools.every(t => selectedMap[t.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const selectedIds = Object.keys(selectedMap);
    const items = selectedIds.map(toolId => ({
      tool_id: toolId,
      quantity: toolQuantities[toolId] || 1
    }));
    if (items.length === 0) { toast.error('Veuillez sélectionner au moins un outil'); return; }
    try {
      const payload = { name: formData.name, description: formData.description || null, items };
      if (editingId) {
        await axios.put(`${API}/kits/${editingId}`, payload);
        toast.success('Kit mis à jour avec succès');
      } else {
        await axios.post(`${API}/kits`, payload);
        toast.success('Kit créé avec succès');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    }
  };

  const openEditDialog = useCallback((kit) => {
    setFormData({ name: kit.name, description: kit.description || '' });
    const sel = {};
    const quantities = {};
    kit.items.forEach(item => { sel[item.tool_id] = true; quantities[item.tool_id] = item.quantity; });
    setSelectedMap(sel);
    setToolQuantities(quantities);
    setEditingId(kit.id);
    setDialogOpen(true);
  }, []);

  const handleDelete = useCallback((kitId) => {
    setDeleteKitId(kitId);
    setConfirmDeleteOpen(true);
  }, []);

  const confirmDelete = async () => {
    if (!deleteKitId) return;
    try {
      await axios.delete(`${API}/kits/${deleteKitId}`);
      toast.success('Kit supprimé avec succès');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setConfirmDeleteOpen(false);
      setDeleteKitId(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-heading font-bold">Kits d'Outils</h1>
        </div>
        <div className="text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Boxes className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-heading font-bold">Kits d'Outils</h1>
        </div>
        {user?.role === 'admin' && (
          <Button data-testid="add-kit-button" onClick={() => { resetForm(); setDialogOpen(true); }} className="rounded-full">
            <Plus className="w-4 h-4 mr-2" /> Nouveau Kit
          </Button>
        )}
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input data-testid="kit-search-input" placeholder="Rechercher un kit..." className="pl-10 rounded-lg max-w-md" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredKits.map((kit) => (
          <Card key={kit.id} data-testid={`kit-card-${kit.id}`} className="hover:shadow-lg transition-shadow duration-200">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Boxes className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-heading">{kit.name}</CardTitle>
                    <Badge variant="secondary" className="mt-1">{kit.items.length} outil{kit.items.length > 1 ? 's' : ''}</Badge>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {kit.description && <p className="text-sm text-muted-foreground">{kit.description}</p>}
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Contenu du kit</p>
                <div className="space-y-1">
                  {kit.items.slice(0, 4).map((item, index) => (
                    <div key={index} className="flex items-center justify-between text-sm bg-muted/50 px-2 py-1 rounded">
                      <div className="flex items-center gap-2">
                        <Wrench className="w-3 h-3 text-muted-foreground" />
                        <span className="truncate">{item.tool_name}</span>
                      </div>
                      <Badge variant="outline" className="text-xs">x{item.quantity}</Badge>
                    </div>
                  ))}
                  {kit.items.length > 4 && (
                    <p className="text-xs text-muted-foreground text-center pt-1">+{kit.items.length - 4} autre{kit.items.length - 4 > 1 ? 's' : ''}</p>
                  )}
                </div>
              </div>
              {user?.role === 'admin' && (
                <div className="flex gap-2 pt-2 border-t">
                  <Button data-testid={`edit-kit-${kit.id}`} onClick={() => openEditDialog(kit)} variant="outline" size="sm" className="flex-1 rounded-full">
                    <Pencil className="w-3 h-3 mr-1" /> Modifier
                  </Button>
                  <Button data-testid={`delete-kit-${kit.id}`} onClick={() => handleDelete(kit.id)} variant="destructive" size="sm" className="rounded-full">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredKits.length === 0 && (
        <div className="text-center py-12">
          <Boxes className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
          <p className="text-muted-foreground">Aucun kit trouvé</p>
          {user?.role === 'admin' && <p className="text-sm text-muted-foreground mt-2">Créez votre premier kit pour faciliter les affectations</p>}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Boxes className="w-5 h-5" />
              {editingId ? 'Modifier le Kit' : 'Nouveau Kit'}
              {selectedCount > 0 && <Badge variant="default" className="ml-2">{selectedCount} outil(s)</Badge>}
            </DialogTitle>
            <DialogDescription className="sr-only">Formulaire de création ou modification d'un kit d'outils</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 flex-1 overflow-hidden flex flex-col">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nom du kit *</Label>
                <Input id="name" data-testid="kit-name-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Ex: Kit Électricien" required className="rounded-lg" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input id="description" data-testid="kit-description-input" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Description optionnelle..." className="rounded-lg" />
              </div>
            </div>

            <div className="space-y-2 flex-1 overflow-hidden flex flex-col">
              <div className="flex items-center justify-between">
                <Label>Sélectionner les outils * ({tools.length} disponibles)</Label>
                <Button type="button" variant="outline" size="sm" onClick={allSelected ? deselectAll : selectAll} className="rounded-full text-xs" data-testid="kit-select-all-button">
                  {allSelected ? (<><Square className="w-3 h-3 mr-1" /> Tout désélectionner</>) : (<><CheckSquare className="w-3 h-3 mr-1" /> Tout sélectionner ({filteredTools.length})</>)}
                </Button>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input data-testid="kit-tool-search" placeholder="Rechercher un outil..." className="pl-10 rounded-lg" value={toolSearchTerm} onChange={(e) => setToolSearchTerm(e.target.value)} />
              </div>

              <div className="border rounded-lg overflow-y-auto flex-1 min-h-[200px] max-h-[300px] divide-y" data-testid="kit-tool-list">
                {filteredTools.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Aucun outil trouvé</p>
                  </div>
                ) : (
                  filteredTools.map(tool => (
                    <ToolRow
                      key={tool.id}
                      tool={tool}
                      isSelected={!!selectedMap[tool.id]}
                      quantity={toolQuantities[tool.id] || 1}
                      onToggle={toggleToolSelection}
                      onQuantityChange={updateToolQuantity}
                    />
                  ))
                )}
              </div>
            </div>

            <Button data-testid="kit-submit-button" type="submit" className="w-full rounded-full" disabled={selectedCount === 0 || !formData.name}>
              <Boxes className="w-4 h-4 mr-2" />
              {editingId ? 'Mettre à jour le kit' : 'Créer le kit'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen} onConfirm={confirmDelete} title="Supprimer le kit" description="Êtes-vous sûr de vouloir supprimer ce kit ? Cette action est irréversible." />
    </div>
  );
};

export default ToolKits;
